import React, { createContext, useContext, useReducer, useEffect, ReactNode, useState, useRef } from 'react';
import { useAuth } from './AuthContext';
import { cartService, CartItem } from '../services/cartService';
import { analytics } from '../lib/posthog';

// Constantes para storage
const GUEST_CART_KEY = 'snatched_guest_cart';
const ZIPCODE_KEY = 'snatched_zipcode';

// Guest cart usa sessionStorage — limpa ao fechar o browser/aba
// Isso evita que itens de sessões anteriores apareçam para utilizadores deslogados
const saveGuestCart = (items: CartItem[]) => {
  try {
    sessionStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
  } catch (error) {
    // ignore
  }
};

const loadGuestCart = (): CartItem[] => {
  try {
    const stored = sessionStorage.getItem(GUEST_CART_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    return [];
  }
};

const clearGuestCart = () => {
  try {
    sessionStorage.removeItem(GUEST_CART_KEY);
    localStorage.removeItem(GUEST_CART_KEY); // limpa dados antigos do localStorage
  } catch (error) {
    // ignore
  }
};

// Funções para gerenciar CEP
const saveZipCode = (zipCode: string) => {
  try {
    localStorage.setItem(ZIPCODE_KEY, zipCode);
  } catch (error) {
    console.error('❌ Erro ao salvar CEP:', error);
  }
};

const loadZipCode = (): string | null => {
  try {
    return localStorage.getItem(ZIPCODE_KEY);
  } catch (error) {
    console.error('❌ Erro ao carregar CEP:', error);
    return null;
  }
};

interface CartState {
  items: CartItem[];
  total: number;
  shipping: number | null;    // null = frete não calculado ou erro
  finalTotal: number;
  itemCount: number;
  isLoading: boolean;
  isUpdating: boolean;
  zipCode: string | null;
  isCalculatingShipping: boolean;
}

interface CartContextType extends CartState {
  addToCart: (productId: string, quantity?: number) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  removeFromCart: (itemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
  setZipCode: (zipCode: string) => Promise<void>;
  calculateShipping: () => Promise<void>;
}

type CartAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_UPDATING'; payload: boolean }
  | { type: 'SET_ITEMS'; payload: CartItem[] }
  | { type: 'UPDATE_ITEM'; payload: { id: string; quantity: number } }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'CLEAR_CART' }
  | { type: 'SET_ZIPCODE'; payload: string | null }
  | { type: 'SET_SHIPPING'; payload: number | null }
  | { type: 'SET_CALCULATING_SHIPPING'; payload: boolean };

const initialState: CartState = {
  items: [],
  total: 0,
  shipping: null,  // Inicialmente null = precisa calcular
  finalTotal: 0,
  itemCount: 0,
  isLoading: false,
  isUpdating: false,
  zipCode: loadZipCode(),
  isCalculatingShipping: false,
};

const calculateTotals = (items: CartItem[], shipping: number | null = 0) => {
  const total = items.reduce((sum, item) => sum + (item.product?.price || 0) * item.quantity, 0);
  const shippingValue = shipping ?? 0; // Usar 0 se null para exibição
  const finalTotal = total + shippingValue;
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  
  return { total, finalTotal, itemCount };
};

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_UPDATING':
      return { ...state, isUpdating: action.payload };

    case 'SET_ITEMS': {
      const items = action.payload;
      const totals = calculateTotals(items, state.shipping);
      return { ...state, items, ...totals };
    }

    case 'UPDATE_ITEM': {
      const updatedItems = state.items.map(item =>
        item.id === action.payload.id ? { ...item, quantity: action.payload.quantity } : item
      );
      const updatedTotals = calculateTotals(updatedItems, state.shipping);
      return { ...state, items: updatedItems, ...updatedTotals };
    }

    case 'REMOVE_ITEM': {
      const filteredItems = state.items.filter(item => item.id !== action.payload);
      const filteredTotals = calculateTotals(filteredItems, state.shipping);
      return { ...state, items: filteredItems, ...filteredTotals };
    }
      
    case 'CLEAR_CART':
      return { ...state, items: [], total: 0, shipping: null, finalTotal: 0, itemCount: 0 };

    case 'SET_ZIPCODE':
      return { ...state, zipCode: action.payload };

    case 'SET_SHIPPING': {
      const totals = calculateTotals(state.items, action.payload);
      return { ...state, shipping: action.payload, ...totals };
    }

    case 'SET_CALCULATING_SHIPPING':
      return { ...state, isCalculatingShipping: action.payload };
      
    default:
      return state;
  }
};

const CartContext = createContext<CartContextType | null>(null);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { user } = useAuth();
  const prevUserRef = useRef<typeof user | undefined>(undefined);

  // Carregar carrinho ao montar ou quando o usuário muda
  useEffect(() => {
    const prevUser = prevUserRef.current;
    prevUserRef.current = user;

    // Logout detectado: usuário estava logado e agora é null → limpar carrinho
    if (prevUser !== undefined && prevUser !== null && user === null) {
      clearGuestCart();
      dispatch({ type: 'CLEAR_CART' });
      return;
    }

    refreshCart();
  }, [user]);

  // Recalcular frete quando CEP ou items mudarem
  useEffect(() => {
    if (state.items.length > 0 && state.zipCode) {
      calculateShipping();
    }
  }, [state.zipCode, state.items.length]);

  const refreshCart = async () => {
    console.log('🔄 RefreshCart chamado, user:', user?.id || 'guest');
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      if (user) {
        // Usuário logado: buscar do Supabase
        const items = await cartService.getCartItems(user.id);
        console.log('👤 Items do usuário logado:', items);
        dispatch({ type: 'SET_ITEMS', payload: items });
      } else {
        // Guest: carregar do localStorage e buscar dados dos produtos
        const guestItems = loadGuestCart();
        console.log('👥 Items guest do localStorage:', guestItems);
        
        // Se tiver items sem dados de produto, buscar do Supabase
        const itemsWithProducts = await Promise.all(
          guestItems.map(async (item) => {
            if (!item.product && item.product_id) {
              console.log('🔍 Buscando produto para item:', item.product_id);
              const productData = await cartService.getProductById(item.product_id);
              return {
                ...item,
                id: item.id || `guest-${Date.now()}-${item.product_id}`,
                product: productData
              };
            }
            return item;
          })
        );
        
        console.log('✅ Items com produtos completos:', itemsWithProducts);
        // Atualizar localStorage com produtos completos
        saveGuestCart(itemsWithProducts);
        dispatch({ type: 'SET_ITEMS', payload: itemsWithProducts });
      }
    } catch (error) {
      console.error('❌ Erro ao carregar carrinho:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const addToCart = async (productId: string, quantity: number = 1) => {
    console.log('🛒 AddToCart chamado:', { productId, quantity, user: user?.id || 'guest' });
    dispatch({ type: 'SET_UPDATING', payload: true });

    try {
      if (user) {
        // Usuário logado
        const success = await cartService.addToCart(user.id, productId, quantity);
        if (success) {
          await refreshCart();
        }
      } else {
        // Guest: gerenciar localmente
        const guestCart = loadGuestCart();
        console.log('📦 Carrinho guest atual:', guestCart);
        const existingItem = guestCart.find(item => item.product_id === productId);

        if (existingItem) {
          existingItem.quantity += quantity;
          console.log('➕ Item existente, aumentando quantidade');
        } else {
          // Buscar dados do produto do Supabase
          console.log('🔍 Buscando produto do Supabase...');
          const productData = await cartService.getProductById(productId);
          console.log('✅ Produto encontrado:', productData);
          
          guestCart.push({
            id: `guest-${Date.now()}-${productId}`,
            user_id: 'guest',
            product_id: productId,
            quantity,
            product: productData,
            created_at: new Date().toISOString()
          });
        }

        saveGuestCart(guestCart);
        console.log('💾 Carrinho salvo:', guestCart);
        dispatch({ type: 'SET_ITEMS', payload: guestCart });
      }
    } catch (error) {
      console.error('❌ Erro ao adicionar ao carrinho:', error);
    } finally {
      dispatch({ type: 'SET_UPDATING', payload: false });
    }
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    dispatch({ type: 'SET_UPDATING', payload: true });

    try {
      if (user) {
        const success = await cartService.updateQuantity(itemId, quantity);
        if (success) {
          dispatch({ type: 'UPDATE_ITEM', payload: { id: itemId, quantity } });
        }
      } else {
        const guestCart = loadGuestCart();
        // For guest cart, itemId can be either product_id or the guest item id
        const item = guestCart.find(i => i.product_id === itemId || i.id === itemId);
        if (item) {
          item.quantity = quantity;
          saveGuestCart(guestCart);
          dispatch({ type: 'UPDATE_ITEM', payload: { id: item.id, quantity } });
        }
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar quantidade:', error);
    } finally {
      dispatch({ type: 'SET_UPDATING', payload: false });
    }
  };

  const removeFromCart = async (itemId: string) => {
    dispatch({ type: 'SET_UPDATING', payload: true });

    try {
      if (user) {
        const success = await cartService.removeItem(itemId);
        if (success) {
          dispatch({ type: 'REMOVE_ITEM', payload: itemId });
        }
      } else {
        const guestCart = loadGuestCart();
        // Find the matching item (by product_id or id) to get its actual id for the reducer
        const matchedItem = guestCart.find(item => item.product_id === itemId || item.id === itemId);
        const filtered = guestCart.filter(item => item.product_id !== itemId && item.id !== itemId);
        saveGuestCart(filtered);
        if (matchedItem) {
          dispatch({ type: 'REMOVE_ITEM', payload: matchedItem.id });
        }
      }
    } catch (error) {
      console.error('❌ Erro ao remover item:', error);
    } finally {
      dispatch({ type: 'SET_UPDATING', payload: false });
    }
  };

  const clearCart = async () => {
    try {
      if (user) {
        await cartService.clearCart(user.id);
      } else {
        clearGuestCart();
      }
      dispatch({ type: 'CLEAR_CART' });
    } catch (error) {
      console.error('❌ Erro ao limpar carrinho:', error);
    }
  };

  const setZipCode = async (zipCode: string) => {
    const cleanZip = zipCode.replace(/\s+/g, '').toUpperCase();
    
    if (cleanZip.length >= 4) {
      saveZipCode(cleanZip);
      dispatch({ type: 'SET_ZIPCODE', payload: cleanZip });
    }
  };

  const calculateShipping = async () => {
    if (!state.zipCode || state.items.length === 0) {
      dispatch({ type: 'SET_SHIPPING', payload: null });
      return;
    }

    dispatch({ type: 'SET_CALCULATING_SHIPPING', payload: true });

    try {
      const shippingCost = await cartService.calculateShipping(state.items, state.zipCode);
      dispatch({ type: 'SET_SHIPPING', payload: shippingCost });
    } catch (error) {
      console.error('❌ Erro ao calcular frete:', error);
      dispatch({ type: 'SET_SHIPPING', payload: null });
    } finally {
      dispatch({ type: 'SET_CALCULATING_SHIPPING', payload: false });
    }
  };

  const value: CartContextType = {
    ...state,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    refreshCart,
    setZipCode,
    calculateShipping,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
